/* eslint-disable react/no-unescaped-entities */
import { Wallet, Building2, CheckCircle2, AlertTriangle, ArrowRight, Calendar, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import GermanyResourceShell from './_components/GermanyResourceShell';

const DESC = 'Germany Blocked Account 2026 for Indian students: €11,904 funding rule, Fintiba vs Expatrio vs Coracle, opening process, withdrawal rules & timelines.';

const PROVIDERS = [
  { name: 'Fintiba',  setup: '€159', monthly: '€9.90/mo',  firstYear: '€277.80', current: 'No',          bundle: 'Fintiba Plus',  best: 'Highest embassy recognition · fastest visa approval' },
  { name: 'Expatrio', setup: '€89',  monthly: '€5/mo',     firstYear: '€149',    current: 'Yes (free)',  bundle: 'Value Package', best: 'Best value · bundled Girokonto (debit card)' },
  { name: 'Coracle',  setup: '€99',  monthly: '€0/mo',     firstYear: '€99',     current: 'No',          bundle: 'Coracle Prime', best: 'Lowest total first-year cost · academic focus' },
  { name: 'Deutsche Bank', setup: '€150', monthly: '€5.90/mo', firstYear: '€220.80', current: 'Yes',     bundle: 'Standard',      best: 'Legacy provider · slowest setup (4–6 weeks)' },
];

const WHY_BLOCKED = [
  { title: 'Funding proof for embassy', body: '€11,904 (= €992 × 12 months) deposited up-front proves you can sustain yourself for one academic year without working illegally.' },
  { title: 'Monthly auto-release',       body: 'Funds are blocked and released in 12 monthly tranches of €992 directly to your German student bank account.' },
  { title: 'Required by §16b Visa law',  body: 'Section 16b of the German Residence Act mandates funding proof — blocked account is the simplest accepted route.' },
];

const STEPS = [
  { step: 1, title: 'Choose a provider',                   body: 'Fintiba (recognition), Expatrio (value), Coracle (cost), or Deutsche Bank (legacy). Decision matters — see comparison table below.' },
  { step: 2, title: 'Apply online (15 minutes)',           body: 'Upload passport, university admission letter and your Indian address. No German address required at this stage.' },
  { step: 3, title: 'Provider issues IBAN',                body: 'You receive your IBAN (German account number) within 1–3 business days for Fintiba/Expatrio/Coracle; 2–4 weeks for Deutsche Bank.' },
  { step: 4, title: 'Transfer €11,904 + setup fee',        body: 'Wire transfer from your Indian bank (e.g. ICICI, HDFC) using A2 form (LRS limit applies). Allow 3–5 business days for funds to land.' },
  { step: 5, title: 'Receive blocked-account confirmation',body: 'PDF confirmation issued the same day funds clear — this is the document you submit at the German embassy for your visa.' },
  { step: 6, title: 'On arrival in Germany',               body: 'Once you receive your German address registration (Anmeldung), the provider activates your account and releases €992/month for 12 months.' },
];

const WITHDRAWAL_RULES = [
  '€992 maximum monthly withdrawal (cannot accelerate)',
  'Funds released only AFTER address registration (Anmeldung)',
  'Excess balance from one month does NOT roll over (use-it-or-lose-it cap)',
  'Provider can freeze the account if proof of enrollment is not received within 3 months',
  'Closing the account before 12 months requires written justification + may forfeit setup fee',
];

const FAQS = [
  { q: 'How much do I need in the blocked account for 2026?',
    a: 'The German embassy currently requires €11,904 (= €992/month × 12 months) for Indian student visa applicants. This figure rises every academic year — verify the latest figure on the German Embassy New Delhi website before transferring funds.' },
  { q: 'Which is the best blocked account provider for Indian students?',
    a: 'Fintiba has the highest embassy recognition and fastest visa approval rates — recommended for first-time applicants. Expatrio offers the best value with a bundled current account (Girokonto). Coracle is the cheapest first-year option but lacks a debit card. Deutsche Bank is the slowest (4–6 weeks setup) and recommended only if you already have a relationship with them.' },
  { q: 'Can I open the blocked account from India?',
    a: 'Yes — all four providers (Fintiba, Expatrio, Coracle, Deutsche Bank) allow 100% online setup from India. You only need a valid passport, university admission letter, and your Indian residential address. The whole process takes 1–5 business days for the IBAN issuance + 3–5 business days for funds to clear via wire transfer.' },
  { q: 'How do I transfer €11,904 from my Indian bank?',
    a: 'Use the LRS (Liberalised Remittance Scheme) facility at your Indian bank. Fill A2 form, declare purpose as "Education abroad – living expenses". Most banks (ICICI, HDFC, SBI) process this in 3–5 business days. Transfer charge is typically ₹500–₹1,500 plus FX conversion spread.' },
  { q: 'Can I close the blocked account once I start working in Germany?',
    a: 'Yes — once you have a German student bank account (Girokonto) and a steady stipend or part-time income, you can request a transfer of any residual balance to your Girokonto and close the blocked account. Most students do this in their 2nd year. Note: closing before 12 months may forfeit your setup fee.' },
  { q: 'What happens if I do not get a German student visa?',
    a: 'If your visa is rejected, all four providers (Fintiba, Expatrio, Coracle, Deutsche Bank) refund 100% of the deposited amount minus a small administrative fee (€50–€150). Refund timeline is 4–8 weeks. Always provide the visa rejection letter to the provider as proof.' },
];

const GermanyBlockedAccountPage = () => (
  <GermanyResourceShell
    slug="blocked-account"
    breadcrumbLabel="Blocked Account"
    articleHeadline="Germany Blocked Account 2026 — Complete Guide for Indian Students"
    articleDescription={DESC}
    faqs={FAQS}
    related={['student-visa', 'cost-of-study', 'aps-certificate']}
  >
    {/* Hero */}
    <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}>
      <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <Wallet className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">Germany Resource — Blocked Account</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne text-white leading-tight mb-4">
            Germany Blocked Account 2026 — €11,904 Funding Proof for Indian Students
          </h1>
          <p className="text-base lg:text-lg text-slate-300 leading-relaxed mb-6">
            The Sperrkonto (blocked account) is the single biggest financial requirement for a German student visa. This is the complete 2026 playbook — €11,904 funding rule, Fintiba vs Expatrio vs Coracle vs Deutsche Bank head-to-head, the 6-step opening process, withdrawal rules, and what happens if your visa is rejected.
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-xl">
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Deposit</div>
              <div className="text-base font-bold text-[#C5A028]">€11,904</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Monthly Release</div>
              <div className="text-base font-bold text-[#C5A028]">€992</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Setup Time</div>
              <div className="text-base font-bold text-[#C5A028]">1–5 days</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Why required */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">Why is a Blocked Account Required?</h2>
        <p className="text-[15px] leading-7 text-slate-700 mb-6">
          A Sperrkonto (literally "blocked account") is a special German bank account where you deposit one year of living expenses up-front. The funds are then released monthly in fixed instalments throughout your first academic year — proving to the German embassy and the Foreigners' Office (Ausländerbehörde) that you can sustain yourself without working illegally.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          {WHY_BLOCKED.map((w, i) => (
            <div key={i} className="bg-blue-50/50 border border-blue-200 rounded-xl p-5">
              <ShieldCheck className="w-7 h-7 text-[#003087] mb-3" />
              <h3 className="text-base font-bold font-syne text-[#003087] mb-2">{w.title}</h3>
              <p className="text-sm text-slate-700 leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Provider comparison */}
    <section className="py-14 lg:py-18 bg-slate-50">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-2">Blocked Account Providers Compared (2026)</h2>
        <p className="text-sm text-slate-600 mb-6">All four providers are accepted by the German embassy in New Delhi. Choose based on cost vs recognition vs bundled features.</p>
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-[#003087] text-white">
              <tr>
                <th className="px-4 py-3 text-left font-syne">Provider</th>
                <th className="px-4 py-3 text-left font-syne">Setup Fee</th>
                <th className="px-4 py-3 text-left font-syne">Monthly Fee</th>
                <th className="px-4 py-3 text-left font-syne">1st-Year Total</th>
                <th className="px-4 py-3 text-left font-syne">Current Acc.</th>
                <th className="px-4 py-3 text-left font-syne">Best For</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {PROVIDERS.map((p) => (
                <tr key={p.name}>
                  <td className="px-4 py-3 font-bold text-[#003087]">{p.name}</td>
                  <td className="px-4 py-3">{p.setup}</td>
                  <td className="px-4 py-3">{p.monthly}</td>
                  <td className="px-4 py-3 font-semibold">{p.firstYear}</td>
                  <td className="px-4 py-3">{p.current}</td>
                  <td className="px-4 py-3 text-slate-700">{p.best}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-blue-50 border-l-4 border-[#003087] p-4 mt-6 rounded-r-lg">
          <div className="text-xs font-bold text-[#003087] uppercase tracking-wide mb-1">ZAFCO Recommendation</div>
          <p className="text-sm text-slate-700">
            For first-time Indian applicants, choose <strong>Fintiba</strong> (best embassy recognition) or <strong>Expatrio</strong> (best value + bundled debit card). Avoid Deutsche Bank unless you have a 6+ month buffer — setup commonly slips to 4–6 weeks.
          </p>
        </div>
      </div>
    </section>

    {/* 6 steps */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-7">6-Step Opening Process</h2>
        <div className="space-y-3">
          {STEPS.map((s) => (
            <div key={s.step} className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex gap-5">
              <div className="w-12 h-12 rounded-xl bg-[#003087] text-white font-bold font-syne text-lg flex items-center justify-center shrink-0">
                {s.step}
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold font-syne text-slate-900 mb-1.5">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Withdrawal rules */}
    <section className="py-14 lg:py-18 bg-slate-50">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">Withdrawal Rules &amp; Restrictions</h2>
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <ul className="space-y-3">
            {WITHDRAWAL_RULES.map((r, i) => (
              <li key={i} className="flex items-start gap-3 text-[15px] text-slate-700">
                <CheckCircle2 className="w-5 h-5 text-[#003087] shrink-0 mt-0.5" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    {/* Currency Risk + Refund Mechanics */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-3">Currency, Tax &amp; Visa-Rejection Mechanics</h2>
        <p className="text-[15px] leading-7 text-slate-700 mb-6">
          Three real-world risks Indian students consistently overlook when planning the blocked account transfer — money mechanics matter as much as the funding rule itself.
        </p>
        <div className="space-y-3">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
            <h3 className="text-base font-bold font-syne text-[#003087] mb-2">Currency Fluctuation Buffer</h3>
            <p className="text-sm text-slate-700 leading-7">
              The €11,904 deposit is in EUR. If the INR/EUR rate moves 3–5% between your visa-filing and your transfer date, you may end up under-funded. Best practice: transfer ₹11.5L equivalent (~€12,150) — the small buffer covers FX slippage and the bank's wire-transfer cost.
            </p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
            <h3 className="text-base font-bold font-syne text-[#003087] mb-2">LRS &amp; Tax Compliance (India)</h3>
            <p className="text-sm text-slate-700 leading-7">
              Under the Liberalised Remittance Scheme, you can send up to USD 250,000/year for education. <strong>TCS (Tax Collected at Source) of 5%</strong> applies above ₹7L unless paid from an education loan — in which case TCS drops to 0.5%. Always classify your wire transfer purpose as "Education abroad — living expenses" (not "personal remittance") on the A2 form to avoid GST/tax disputes later.
            </p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
            <h3 className="text-base font-bold font-syne text-[#003087] mb-2">Visa Rejection — Refund Timeline</h3>
            <p className="text-sm text-slate-700 leading-7">
              If your German student visa is refused, share the official rejection letter with your provider within 30 days. Fintiba refunds in 4–6 weeks, Expatrio in 6–8 weeks, Coracle in 4 weeks, Deutsche Bank in 8–10 weeks. Administrative fee deducted: €50–€150 (setup fee is forfeited). Funds return to your original Indian account.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-7">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="group bg-slate-50 border border-slate-200 rounded-xl p-5">
              <summary className="font-syne font-semibold text-slate-900 cursor-pointer text-[15px] flex items-start justify-between">
                <span className="pr-6">{f.q}</span>
                <span className="text-[#003087] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm leading-7 text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  </GermanyResourceShell>
);

export default GermanyBlockedAccountPage;
